/**
 * All the  recoil selectors relating to stacks
 * @module selectors
 * @category State
 */
import { type SDK_Stack, type SDK_StackCfKey, clientConfig, demoImages, demoStackCfKeys, getSdk, getStackFromCfKey, strings } from "@quickpoint.me/shared";
import { selector, selectorFamily } from "recoil";

import { getAuth, getIdToken } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { cloneDeep } from "lodash-es";
import { utilityFileNameMessage } from "../lib/codeMessages";
import { Authorization } from "./authAtoms";
import { OwnerName } from "./authSelectors";
import { CodeMirrorCodeState } from "./codeAtom";
import { CombinedFileMapsSelector } from "./files";
import { FilterMapSelector } from "./filters";
import { PublishAtom } from "./publish";
import { StackAtomFamily, StackCfKeyAtom } from "./stackAtoms";
import { DemoMode } from "./uiAtoms";

export const BackLinksSelector = selector<string[]>({
  key: "BackLinks",
  get: ({ get }) => {
    const stackCfKeys = get(StackCfKeysSelector);
    const ownerName = get(OwnerName);
    if (ownerName) {
      let backLinks = stackCfKeys.map(cfKey => cfKey?.key.replace(`${ownerName}/`, ""));
      backLinks = backLinks.filter(el => el) as string[];
      return backLinks;
    }
    return [];
  },
});

export const StackId = selector<SDK_Stack["stackId"]>({
  key: "StackId",
  get: async ({ get }) => {
    // console.log("StackId selector called");
    const stackCfKey = get(StackCfKeyAtom);
    const isLoggedIn = get(Authorization).isLoggedIn
    if (!stackCfKey || !isLoggedIn) return strings.unset;
    const stack = get(StackAtomFamily(stackCfKey));
    return stack.stackId;
  },
});

/**
 * This combines the stackCfKey, code and images in an object
 * shaped like the result from the server when a stack is requested.
 * Splitting the code out was necessary as CodeMirror 6 lives outside of the Reactiverse
 * in this app. It's easier to deal with the composite parts of a Stack separately.
 *
 * It also broadcasts the latest stack to any viewers listening.
 */



export const ComposedStack = selector<SDK_Stack>({
  key: "Stack",
  get: ({ get }) => {
    // console.log("ComposedStack selector called");
    const demoMode = get(DemoMode)
    const stackCfKey = get(StackCfKeyAtom);
    const isLoggedIn = get(Authorization).isLoggedIn
    if (!demoMode && (!stackCfKey || !isLoggedIn)) return errorStack;


    const { audioFiles, dataFiles, images } = get(CombinedFileMapsSelector)
    const code = get(CodeMirrorCodeState(stackCfKey));
    const filters = get(FilterMapSelector);
    const publish = get(PublishAtom);
    const stack = get(StackAtomFamily(stackCfKey));

    return {
      audioFiles,
      code,
      created: stack.created,
      dataFiles,
      filters,
      images,
      ownerName: stack.ownerName ? stack.ownerName : strings.common.unauthorized,
      publish,
      stackCfKey: stack.stackCfKey,
      stackId: stack.stackId,
      updated: stack.updated,
    };
  },
});


async function getStackCfKeys(ownerName: string, jwt: string) {
  const client = new GraphQLClient(clientConfig.gqlGetStack());
  const sdk = getSdk(client);
  try {
    const res = await sdk.GetStackCfKeys({ ownerName, jwt });
    if (res.getStackCfKeys && res.getStackCfKeys.__typename === "StackCfKeys") {
      // add ownerName to help files
      for (const stackCfKey of res.getStackCfKeys.stackCfKeys) {
        if (stackCfKey.key.startsWith(strings.url.helpUrl)) {
          stackCfKey.key = `${ownerName}/${stackCfKey.key}`;
        }
      }

      return res.getStackCfKeys.stackCfKeys;
    }
    return []
  } catch (error) {
    console.error(error);
    return [];
  }
}




export const StackCfKeysSelector = selector<SDK_StackCfKey[]>({
  key: "GetStackCfKeys",
  get: async ({ get }) => {
    const ownerName = get(OwnerName);
    if (!ownerName) return [];
    if (ownerName === "demo") {
      return demoStackCfKeys
    }

    const user = getAuth().currentUser
    if (!user) return []
    const jwt = await getIdToken(user);
    if (!jwt) return [];

    if (ownerName === strings.common.unauthorized) return [];
    const res = await getStackCfKeys(ownerName, jwt);
    return res


  },
  // cachePolicy_UNSTABLE: { eviction: "most-recent" },
});

export const StackPathSelector = selector({
  key: "StackPath",
  get: ({ get }) => {
    const stackCfKey = get(StackCfKeyAtom);
    return stackCfKey;
  },
});

export const StackFileNameSelectorFamily = selectorFamily<string, string>({
  key: "StackFileName",
  get: stackCfKey => () => {
    const parts = stackCfKey.split("/");
    const stackFileName = parts[parts.length - 1];
    return stackFileName;
  },
});

export const StackSelectorFamily = selectorFamily<SDK_Stack, string>({
  key: "StackSelectorFamily",
  get: stackCfKey => ({ get }) => {
    const demoMode = get(DemoMode)
    // In demo mode, read StackAtomFamily directly (primed by BabySitter on
    // demo entry). This lets setNewCode writes propagate back through the
    // normal Recoil path so code survives CM remounts across tab switches.
    if (demoMode) return get(StackAtomFamily(stackCfKey));
    const ownerName = get(OwnerName);
    if (!ownerName) return errorStack;
    const stackFileName = get(StackFileNameSelectorFamily(stackCfKey));

    // set the code to any error messages based on the stackFileName
    if (!stackCfKey || stackFileName in utilityFileNameMessage) {
      const sfn = stackFileName as keyof typeof utilityFileNameMessage;
      const oN = ownerName as keyof typeof utilityFileNameMessage;
      const fileError = utilityFileNameMessage[sfn];
      const ownerError = utilityFileNameMessage[oN];

      const code = fileError ? fileError : ownerError ? ownerError : "";
      const newErrorStack = cloneDeep(errorStack);
      newErrorStack.code = code;
      return newErrorStack;
    }

    if (!getStackFromCfKey(stackCfKey).validStackCfKey) return errorStack;
    const stack = get(StackAtomFamily(stackCfKey));
    return stack;
  },
  set: stackCfKey => ({ set }, newValue) => {
    set(StackAtomFamily(stackCfKey), oldVal => {
      const old = cloneDeep(oldVal);
      const newStack = { ...old, ...newValue };
      return newStack;
    })
  }
});



export const errorStack: SDK_Stack = {
  audioFiles: [],
  code: "",
  filters: [],
  images: [],
  dataFiles: [],
  ownerName: strings.common.unauthorized,
  publish: false,
  created: 666,
  updated: 79371604,
  stackCfKey: strings.unset,
  stackId: strings.unset,
};


export const demoStack: SDK_Stack = {
  audioFiles: [],
  code: "",
  filters: [],
  images: demoImages as SDK_Stack["images"],
  dataFiles: [],
  ownerName: strings.demo.ownerName,
  publish: false,
  created: 666,
  updated: 79371604,
  stackCfKey: strings.demo.cfKey,
  stackId: strings.demo.cfKey,
};


export const emptyStack: SDK_Stack = {
  audioFiles: [],
  code: " ",
  filters: [],
  images: [],
  dataFiles: [],
  ownerName: strings.demo.ownerName,
  publish: false,
  created: 666,
  updated: 79371604,
  stackCfKey: "empty",
  stackId: "empty",
};
